// Angular
import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

// RxJS
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Data Contracts
import { DocumentHistoryResponse } from '@data-contracts';

// Local
import { DocumentsApiService } from '@app/services/api/documents/documents-api.service';
import { ErrorsStateService } from '@app/services/ui-state/errors-state/errors-state.service';

/**
 * Resolves the document history for the {@link DocumentHistoryComponent} before the
 * `history/:documentId` outlet route activates.
 *
 * Moving the fetch here (from the component constructor) ensures the response is
 * present at construction time and is painted in the in-zone navigation tick.
 * This eliminates the stale-until-next-event blank panel that occurred when the
 * OnPush component fetched data after construction on a resolver-less secondary outlet.
 *
 * On error the user is notified via a snack-bar and the outlet activation is
 * cancelled (EMPTY), so the panel never opens with blank content.
 */
export const documentHistoryResolver: ResolveFn<DocumentHistoryResponse> = route => {
  const documentsApiService = inject(DocumentsApiService);
  const errorsStateService = inject(ErrorsStateService);

  const documentId = Number(route.paramMap.get('documentId'));
  if (!documentId) {
    console.error('documentId is missing in the route parameters.');
    return EMPTY;
  }

  return documentsApiService.getDocumentHistory(documentId).pipe(
    catchError(err => {
      console.error('An error occurred while fetching document history.', err);
      errorsStateService.showError('An error occurred while fetching document history.');
      return EMPTY;
    }),
  );
};
